import React from 'react';
import { Box, InputAdornment, InputBase, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';

import { useCommandPaletteControls } from '../../shared/CommandPalette/CommandPaletteHost';
import { ShortcutHint } from '../../shared/CommandPalette/SearchTrigger';
import { useWidgetFrame } from '../frameContext';

/**
 * Quick search widget: a front door for the global search (command palette). Clicking the
 * field opens the palette; typing a character opens it with that character already in the
 * palette's input, so the widget never keeps a search of its own. One search implementation,
 * same coverage (work orders, customers, vehicles, invoices, parts) and the same result rows
 * everywhere.
 */
export default function SearchBody() {
  const { t } = useTranslation();
  const frame = useWidgetFrame();
  const { openPalette } = useCommandPaletteControls();
  const inert = Boolean(frame?.inPreview) || frame?.mode === 'edit';
  const bare = frame?.size === 'R';

  const open = (query) => {
    if (inert) return;
    openPalette(query || '');
  };

  const onKeyDown = (event) => {
    if (inert) return;
    if (event.key === 'Enter') {
      event.preventDefault();
      open('');
      return;
    }
    if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
      event.preventDefault();
      open(event.key);
    }
  };

  return (
    <Box sx={{ flex: 1, minHeight: 0, px: bare ? 1.25 : 1.5, py: bare ? 1.25 : 1.25, display: 'flex', flexDirection: 'column', gap: 0.75, justifyContent: 'center' }}>
      <InputBase
        readOnly
        fullWidth
        value=""
        placeholder={t('dashboard.search.placeholder')}
        onClick={() => open('')}
        onKeyDown={onKeyDown}
        inputProps={{ 'data-testid': 'widget-search-input', 'aria-label': t('commandPalette.openSearch') }}
        startAdornment={
          <InputAdornment position="start">
            <SearchIcon sx={{ fontSize: 20, color: '#94a3b8' }} />
          </InputAdornment>
        }
        endAdornment={
          <InputAdornment position="end">
            <ShortcutHint />
          </InputAdornment>
        }
        sx={{
          height: 40,
          px: 1.5,
          borderRadius: '10px',
          border: '1px solid #e2e8f0',
          bgcolor: '#fff',
          fontSize: 14,
          cursor: inert ? 'default' : 'text',
          '& input': { cursor: inert ? 'default' : 'text', caretColor: 'transparent' },
          '&:hover': inert ? {} : { borderColor: '#3182ce' },
          '&.Mui-focused': { borderColor: '#3182ce', boxShadow: '0 0 0 3px rgba(49,130,206,0.15)' },
        }}
      />
      {!bare ? (
        <Typography sx={{ fontSize: 12, color: 'text.secondary', px: 0.5 }} noWrap>
          {t('dashboard.search.coverage')}
        </Typography>
      ) : null}
    </Box>
  );
}
